// packages/web/src/components/teach/MaterialUploadSection.tsx
import React, { useEffect, useState } from 'react';
import { useTeacherLearningStore } from '../../stores/teacherLearningStore';
import { teacherLearningApi } from '../../services/teacherLearningApi';

export const MaterialUploadSection: React.FC = () => {
  const {
    materialText,
    skipMaterial,
    historyMaterials,
    currentStep,
    step2Status,
    exercises,
    setMaterialText,
    setSkipMaterial,
    setHistoryMaterials,
    setExercises,
    setStepStatus,
    setErrorMessage,
    setCurrentStep,
  } = useTeacherLearningStore();

  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string>('');
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);

  // 加载历史教案
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const response = await teacherLearningApi.getTeachingMaterialsHistory(10);
        if (response.success && response.data) {
          setHistoryMaterials(response.data.materials || []);
        }
      } catch (error) {
        console.error('Failed to load history materials:', error);
      }
    };

    if (currentStep >= 2) {
      loadHistory();
    }
  }, [currentStep, setHistoryMaterials]);

  // 处理历史教案选择
  const handleHistorySelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const materialId = e.target.value;
    setSelectedHistoryId(materialId);

    if (!materialId) {
      setMaterialText('');
      setUploadedFileName('');
      setFilePreviewUrl(null);
      return;
    }

    try {
      const response = await teacherLearningApi.getTeachingMaterialContent(materialId);
      if (response.success && response.data) {
        setMaterialText(response.data.content || '');
        // 更新使用时间
        await teacherLearningApi.updateTeachingMaterialUsage(materialId);
      }
    } catch (error: any) {
      setErrorMessage('加载教案失败：' + error.message);
    }
  };

  // 处理文件上传
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFileName(file.name);

    // 如果是图片，创建预览
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
    } else {
      setFilePreviewUrl(null);
    }

    // 读取文件内容
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (file.type.startsWith('text/') || file.name.endsWith('.txt')) {
        // 文本文件直接显示
        setMaterialText(content);
      } else {
        // 其他文件类型保存为base64
        setMaterialText(content);
      }
    };

    if (file.type.startsWith('text/') || file.name.endsWith('.txt')) {
      reader.readAsText(file);
    } else {
      reader.readAsDataURL(file);
    }
  };

  // 处理删除文件
  const handleDeleteFile = () => {
    setUploadedFileName('');
    setFilePreviewUrl(null);
    setMaterialText('');
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }
  };

  // 处理生成教学要求
  const handleGenerateRequirements = async () => {
    if (exercises.length === 0) {
      setErrorMessage('请先上传并识别习题集');
      return;
    }

    setStepStatus(2, 'loading');
    setIsProcessing(true);

    try {
      const response = await teacherLearningApi.generateRequirements({
        exercises,
        materialText: materialText || undefined,
      });

      if (response.success && response.data) {
        // 设置 originalTeacherRequirement 为生成的值
        const exercisesWithOriginal = response.data.exercises.map((ex: any) => ({
          ...ex,
          originalTeacherRequirement: ex.teacher_requirement,
        }));
        setExercises(exercisesWithOriginal);
        setStepStatus(2, 'success');
        setCurrentStep(3);
      } else {
        throw new Error(response.error?.message || '生成失败');
      }
    } catch (error: any) {
      setStepStatus(2, 'error');
      setErrorMessage(error.message || '生成教学要求失败');
    } finally {
      setIsProcessing(false);
    }
  };

  // 处理跳过步骤
  const handleSkip = () => {
    // 跳过时也设置 originalTeacherRequirement 为空字符串
    const exercisesWithOriginal = exercises.map(ex => ({
      ...ex,
      originalTeacherRequirement: ex.teacher_requirement || '',
    }));
    setExercises(exercisesWithOriginal);
    setStepStatus(2, 'skipped');
    setCurrentStep(3);
  };

  if (currentStep < 2) {
    return null;
  }

  return (
    <div className="material-upload-section bg-white rounded-lg shadow p-6 h-full flex flex-col">
      {/* 头部：标题 + 选择文件按钮 + 状态提示 */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          步骤2：上传教案资料（可选）
        </h2>
        
        <div className="flex items-center gap-3">
          {/* 状态提示 */}
          {step2Status === 'loading' && (
            <span className="text-sm text-blue-600">处理中...</span>
          )}
          {step2Status === 'success' && (
            <span className="text-sm text-green-600">✓ 生成成功</span>
          )}
          {step2Status === 'error' && (
            <span className="text-sm text-red-600">✗ 生成失败</span>
          )}
          {step2Status === 'skipped' && (
            <span className="text-sm text-gray-600">已跳过</span>
          )}
          
          {/* 选择文件按钮 */}
          {!uploadedFileName && (
            <label className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 cursor-pointer transition-colors">
              选择文件
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt,image/*"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="hidden"
              />
            </label>
          )}
        </div>
      </div>

      {/* 历史教案选择 */}
      {historyMaterials.length > 0 && !uploadedFileName && (
        <div className="mb-4">
          <select
            value={selectedHistoryId}
            onChange={handleHistorySelect}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="">-- 或选择历史教案 --</option>
            {historyMaterials.map((material) => (
              <option key={material.id} value={material.id}>
                {material.content_summary} ({new Date(material.last_used_at).toLocaleDateString()})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 预览区域 */}
      <div className="flex-1 min-h-0 mb-4">
        {!uploadedFileName && !selectedHistoryId ? (
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center h-full flex flex-col items-center justify-center">
            <div className="text-4xl mb-4">📄</div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              选择教案文件或从历史记录中选择
            </p>
            <p className="text-xs text-gray-500">
              支持 PDF、Word、文本文件或图片格式
            </p>
          </div>
        ) : (
          <div className="relative h-full border border-gray-200 rounded-xl overflow-hidden">
            {filePreviewUrl ? (
              // 图片预览
              <div className="relative h-full">
                <img
                  src={filePreviewUrl}
                  alt="教案预览"
                  className="w-full h-full object-contain"
                />
                {!isProcessing && (
                  <button
                    onClick={handleDeleteFile}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm hover:bg-red-600 transition-colors shadow-lg"
                    title="删除文件"
                  >
                    ✕
                  </button>
                )}
              </div>
            ) : (
              // 文件信息显示
              <div className="h-full flex flex-col items-center justify-center p-6 bg-gray-50">
                <div className="text-5xl mb-4">📄</div>
                <p className="text-sm font-medium text-gray-900 mb-2">{uploadedFileName}</p>
                <p className="text-xs text-gray-500 mb-4">
                  {materialText ? `${Math.min(materialText.length, 100)} 字符` : '文件已加载'}
                </p>
                {!isProcessing && (
                  <button
                    onClick={handleDeleteFile}
                    className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  >
                    删除文件
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 操作按钮 */}
      <div className="flex gap-3">
        <button
          onClick={handleGenerateRequirements}
          disabled={isProcessing || (!materialText && !skipMaterial)}
          className="flex-1 px-6 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {isProcessing ? '处理中...' : '生成教学要求'}
        </button>
        <button
          onClick={handleSkip}
          disabled={isProcessing}
          className="flex-1 px-6 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
        >
          跳过此步骤
        </button>
      </div>
    </div>
  );
};
